// ============================================================================
// INTERESTED CANDIDATES — Tier 1 view of people interested in becoming a
// facilitator. Certifying a candidate with an account takes them off the list.
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { Loader, Search, Trash2, BadgeCheck } from 'lucide-react';
import {
  listFacilitatorCandidates,
  removeFacilitatorCandidate,
  certifyUser,
  FacilitatorCandidate,
  FacilitatorTier,
  TIER_LABELS
} from '../../services/studio-bookings';
import { formatDate } from './FacilitatorParts';

const actionBtn = 'flex items-center gap-1.5 rounded-xl h-9 px-3.5 border border-slate-200 bg-white text-xs font-bold shadow-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

export default function InterestedCandidates({ reloadKey, onChanged, onMessage, onError }: {
  reloadKey: number;
  onChanged: () => void;
  onMessage: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [candidates, setCandidates] = useState<FacilitatorCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  function load() {
    listFacilitatorCandidates()
      .then(setCandidates)
      .catch(() => onError('Failed to load interested candidates'))
      .finally(() => setLoading(false));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [reloadKey]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return candidates.filter(c => !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q));
  }, [candidates, search]);

  async function handleRemove(c: FacilitatorCandidate) {
    if (!window.confirm(`Remove ${c.name} from the interested list?`)) return;
    setBusy(c.candidateId);
    try {
      await removeFacilitatorCandidate(c.candidateId);
      onMessage(`${c.name} removed from the interested list`);
      load();
    } catch (err: any) {
      onError(err.response?.data?.error || 'Failed to remove candidate');
    } finally {
      setBusy(null);
    }
  }

  async function handleCertify(c: FacilitatorCandidate, tier: FacilitatorTier) {
    if (!c.userId || !window.confirm(`Certify ${c.name} as ${TIER_LABELS[tier]}? They will leave the interested list.`)) return;
    setBusy(c.candidateId);
    try {
      await certifyUser(c.userId, tier);
      onMessage(`${c.name} certified as ${TIER_LABELS[tier]}`);
      load();
      onChanged();
    } catch (err: any) {
      onError(err.response?.data?.error || 'Failed to certify');
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return <div className="flex justify-center h-40 items-center"><Loader className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-[#e2e8f0]">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full border border-[#e2e8f0] rounded-xl pl-9 pr-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
          </div>
        </div>
        {visible.length === 0 ? (
          <div className="text-center py-12 text-slate-400 text-sm">
            {candidates.length === 0 ? 'Nobody has registered interest yet' : 'No candidates match this search'}
          </div>
        ) : (
          <table className="w-full table-fixed">
            <thead>
              <tr className="border-b border-[#e2e8f0] bg-slate-50/50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 w-[28%]">Name</th>
                <th className="px-3 py-3 w-24">Account</th>
                <th className="px-3 py-3">Added</th>
                <th className="px-3 py-3 hidden lg:table-cell">Notes</th>
                <th className="px-3 py-3 w-[300px]" />
              </tr>
            </thead>
            <tbody>
              {visible.map(c => (
                <tr key={c.candidateId} className="border-b border-[#e2e8f0] last:border-b-0">
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-navy truncate">{c.name}</p>
                    <p className="text-xs text-slate-500 truncate">{c.email}</p>
                  </td>
                  <td className="px-3 py-3">
                    {c.userId
                      ? <span className="text-xs font-semibold text-emerald-700">Yes</span>
                      : <span className="text-xs text-slate-400">No account</span>}
                  </td>
                  <td className="px-3 py-3">
                    <p className="text-sm text-navy">{formatDate(c.addedAt)}</p>
                    <p className="text-xs text-slate-400 truncate">
                      {c.source === 'self' ? 'Self-registered' : `Nominated by ${c.addedByName}`}
                    </p>
                    {c.trainingAttendedCount > 0 && (
                      <p className="text-xs text-blue-700">
                        {c.trainingAttendedCount} training session{c.trainingAttendedCount === 1 ? '' : 's'} attended
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-3 hidden lg:table-cell">
                    <p className="text-xs text-slate-500 line-clamp-2" title={c.notes}>{c.notes || '—'}</p>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {c.userId ? (
                        <button onClick={() => handleCertify(c, 'tier2')} disabled={busy === c.candidateId} className={`${actionBtn} text-navy hover:bg-slate-50`}>
                          <BadgeCheck className="w-3.5 h-3.5" /> Certify {TIER_LABELS.tier2}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400" title="Certification needs a platform account">Needs an account to certify</span>
                      )}
                      <button onClick={() => handleRemove(c)} disabled={busy === c.candidateId} className={`${actionBtn} text-red-600 hover:bg-red-50`}>
                        {busy === c.candidateId ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-xs text-slate-400">
        {visible.length} of {candidates.length} interested. Training attended by people without an account moves onto their training record when they sign in with the same email.
      </p>
    </div>
  );
}
